import React, { useState, useEffect, useRef } from 'react';
// Aliased to a capitalized name so the app's no-unused-vars rule (which cannot
// see JSX usage without eslint-plugin-react) does not flag it.
import { motion as Motion } from 'framer-motion';
import { Plus, Search, Pencil, Trash2, User, KeyRound, ShieldCheck, X, Loader2, Copy, Check, UserRound } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import Swal from 'sweetalert2';
import withReactContent from 'sweetalert2-react-content';
import { toast } from 'react-toastify';
import { useTranslation } from 'react-i18next';
import Pagination from '../../components/Pagination';
import CommonLoader from '../../components/CommonLoader';
import {
    getPlatformStaffList, getPlatformRoles, createPlatformStaff, updatePlatformStaff,
    setPlatformStaffStatus, deletePlatformStaff, resetPlatformStaffPassword,
} from '../../redux/platformStaffSlice';

const MySwal = withReactContent(Swal);

const emailRe = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const EMPTY_FORM = { name: '', surname: '', email: '', phone: '', designation: '', platformRoleId: '' };

// ---------------------------------------------------------------------------
// Create / edit modal. One modal for both, mirroring the tenant app's AddUser:
// the field set is identical and the only differences are the title, the
// endpoint, and whether a temporary password comes back. Inline rather than a
// separate component file — the platform form is small (no departments,
// council numbers or photos: the platform team signs nothing clinical).
// ---------------------------------------------------------------------------
function AddPlatformUser({ isOpen, onClose, onSaved, editing = null, roles = [] }) {
    const { t } = useTranslation();
    const dispatch = useDispatch();
    const mutating = useSelector((s) => s.platformStaff?.mutating);

    const [form, setForm] = useState(EMPTY_FORM);
    const [errors, setErrors] = useState({});
    // Shown after creating: mail is frequently misconfigured on a fresh
    // deployment, and the Super Admin needs to be able to read the password out
    // rather than discover an hour later it never arrived.
    const [tempPassword, setTempPassword] = useState(null);
    const [copied, setCopied] = useState(false);

    const isEdit = Boolean(editing);
    // Only active roles can be assigned — an account holding an inactive role is
    // blocked from its first login, which looks like a broken account.
    const assignableRoles = (roles || []).filter((r) => r.status === 'active' || r.platformRoleId === editing?.platformRoleId);

    useEffect(() => {
        if (!isOpen) return;
        setTempPassword(null);
        setCopied(false);
        setErrors({});
        if (editing) {
            setForm({
                name: editing.name || '', surname: editing.surname || '', email: editing.email || '',
                phone: editing.phone || '', designation: editing.designation || '',
                platformRoleId: editing.platformRoleId || '',
            });
        } else {
            setForm(EMPTY_FORM);
        }
    }, [isOpen, editing]);

    if (!isOpen) return null;

    const set = (k, v) => {
        setForm((f) => ({ ...f, [k]: v }));
        setErrors((e) => ({ ...e, [k]: undefined }));
    };

    const validate = () => {
        const e = {};
        if (!form.platformRoleId) e.platformRoleId = t('platformUsers.role_required', 'Pick the role this user will hold');
        if (!form.name.trim()) e.name = t('platformUsers.name_required', 'Name is required');
        if (!form.email.trim()) e.email = t('platformUsers.email_required', 'Email is required');
        else if (!emailRe.test(form.email.trim())) e.email = t('platformUsers.email_invalid', 'Enter a valid email');
        setErrors(e);
        return Object.keys(e).length === 0;
    };

    const submit = async () => {
        if (!validate()) return;
        const payload = {
            name: form.name.trim(),
            surname: form.surname.trim() || null,
            email: form.email.trim(),
            phone: form.phone.trim() || null,
            designation: form.designation.trim() || null,
            platformRoleId: form.platformRoleId,
        };

        try {
            if (isEdit) {
                await dispatch(updatePlatformStaff({ platformStaffId: editing.platformStaffId, data: payload })).unwrap();
                toast.success(t('platformUsers.update_success', 'User updated'));
                onClose?.();
                onSaved?.();
                return;
            }

            const result = await dispatch(createPlatformStaff(payload)).unwrap();
            onSaved?.();
            // Hold the modal open on the password panel instead of closing —
            // closing it would throw the one copy of the credentials away.
            setTempPassword(result?.tempPassword || null);
            if (!result?.tempPassword) {
                toast.success(t('platformUsers.create_success', 'User created'));
                onClose?.();
            }
        } catch (err) {
            toast.error(typeof err?.error === 'string' ? err.error : t('platformUsers.save_error', 'Failed to save the user'));
        }
    };

    const copyPassword = async () => {
        try {
            await navigator.clipboard.writeText(tempPassword);
            setCopied(true);
            setTimeout(() => setCopied(false), 2000);
        } catch {
            toast.warn(t('platformUsers.copy_failed', 'Could not copy — select the text and copy it manually.'));
        }
    };

    const field = 'w-full px-3 py-2.5 text-sm border rounded-xl focus:outline-none focus:ring-2 focus:ring-[#4ABDBD]';

    // --- credentials panel, shown after a successful create ------------------
    if (tempPassword) {
        return (
            <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/40 p-4" data-testid="add-platform-user-created">
                <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md p-6 text-center">
                    <div className="mx-auto mb-4 w-14 h-14 rounded-2xl bg-green-100 text-green-600 flex items-center justify-center">
                        <Check className="w-7 h-7" />
                    </div>
                    <h2 className="text-lg font-bold text-[#1a365d] mb-1">{t('platformUsers.user_created', 'User created')}</h2>
                    <p className="text-sm text-slate-500 mb-5">
                        {t('platformUsers.created_hint', 'The credentials were emailed. Copy them now in case the email does not arrive.')}
                    </p>
                    <div className="bg-gray-50 rounded-xl border border-gray-200 p-4 text-left space-y-2 mb-5">
                        <div className="text-xs text-slate-500">{t('platformUsers.username', 'Username')}</div>
                        <div className="text-sm font-mono text-gray-800 break-all">{form.email.trim()}</div>
                        <div className="text-xs text-slate-500 pt-2">{t('platformUsers.temp_password', 'Temporary password')}</div>
                        <div className="flex items-center gap-2">
                            <code className="text-sm font-mono text-gray-800 bg-white px-2 py-1 rounded border border-gray-200 flex-1 break-all">{tempPassword}</code>
                            <button type="button" onClick={copyPassword} data-testid="copy-temp-password"
                                className="p-2 rounded-lg text-[#4ABDBD] hover:bg-[#4ABDBD]/10" title={t('platformUsers.copy', 'Copy')}>
                                {copied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                            </button>
                        </div>
                    </div>
                    <button type="button" onClick={() => { setTempPassword(null); onClose?.(); }}
                        data-testid="add-platform-user-done"
                        className="w-full px-4 py-3 rounded-xl text-white font-semibold text-sm bg-gradient-to-r from-[#4ABDBD] via-[#3AA8A8] to-[#2D8A8A] hover:shadow-lg">
                        {t('platformUsers.done', 'Done')}
                    </button>
                </div>
            </div>
        );
    }

    return (
        <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/40 p-4" data-testid="add-platform-user-modal">
            <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
                <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100">
                    <h2 className="text-lg font-bold text-[#1a365d]">
                        {isEdit ? t('platformUsers.edit_user', 'Edit user') : t('platformUsers.add_user', 'Add user')}
                    </h2>
                    <button type="button" onClick={onClose} className="text-gray-400 hover:text-gray-600" data-testid="add-platform-user-close">
                        <X className="w-5 h-5" />
                    </button>
                </div>

                <div className="flex flex-col items-center gap-2 pt-5">
                    <div className="w-20 h-20 rounded-full bg-gray-100 border-2 border-[#4ABDBD]/40 flex items-center justify-center">
                        <UserRound className="w-9 h-9 text-gray-300" />
                    </div>
                    <span className="text-xs text-slate-400">
                        {t('platformUsers.platform_team_member', 'Platform team member')}
                    </span>
                </div>

                <div className="p-5 grid grid-cols-1 sm:grid-cols-2 gap-4">
                    {/* The role comes first — it decides what the account can do the
                        moment it exists. */}
                    <div className="sm:col-span-2">
                        <label className="text-xs font-medium text-slate-500">{t('platformUsers.role', 'Role')} *</label>
                        <select data-testid="add-platform-user-role" value={form.platformRoleId}
                            onChange={(e) => set('platformRoleId', e.target.value)}
                            className={`${field} ${errors.platformRoleId ? 'border-red-400' : 'border-gray-300'}`}>
                            <option value="">{t('platformUsers.select_role', 'Select a role')}</option>
                            {assignableRoles.map((r) => (
                                <option key={r.platformRoleId} value={r.platformRoleId}>{r.name}</option>
                            ))}
                        </select>
                        {errors.platformRoleId
                            ? <p className="text-xs text-red-500 mt-1">{errors.platformRoleId}</p>
                            : <p className="text-[11px] text-slate-400 mt-1">
                                {t('platformUsers.role_hint', 'The role decides what this person can open and change. Manage roles under Account → Roles.')}
                            </p>}
                    </div>

                    <div>
                        <label className="text-xs font-medium text-slate-500">{t('platformUsers.first_name', 'First name')} *</label>
                        <input data-testid="add-platform-user-name" value={form.name} onChange={(e) => set('name', e.target.value)}
                            className={`${field} ${errors.name ? 'border-red-400' : 'border-gray-300'}`} />
                        {errors.name && <p className="text-xs text-red-500 mt-1">{errors.name}</p>}
                    </div>
                    <div>
                        <label className="text-xs font-medium text-slate-500">{t('platformUsers.surname', 'Surname')}</label>
                        <input data-testid="add-platform-user-surname" value={form.surname} onChange={(e) => set('surname', e.target.value)}
                            className={`${field} border-gray-300`} />
                    </div>
                    <div>
                        <label className="text-xs font-medium text-slate-500">{t('platformUsers.email', 'Email')} *</label>
                        <input data-testid="add-platform-user-email" type="email" value={form.email} onChange={(e) => set('email', e.target.value)}
                            className={`${field} ${errors.email ? 'border-red-400' : 'border-gray-300'}`} />
                        {errors.email && <p className="text-xs text-red-500 mt-1">{errors.email}</p>}
                    </div>
                    <div>
                        <label className="text-xs font-medium text-slate-500">{t('platformUsers.phone', 'Phone')}</label>
                        <input data-testid="add-platform-user-phone" type="tel" value={form.phone}
                            onChange={(e) => set('phone', e.target.value.replace(/[^\d+ ]/g, ''))}
                            maxLength={16}
                            className={`${field} border-gray-300`} />
                    </div>
                    <div className="sm:col-span-2">
                        <label className="text-xs font-medium text-slate-500">{t('platformUsers.designation', 'Designation')}</label>
                        <input data-testid="add-platform-user-designation" value={form.designation} onChange={(e) => set('designation', e.target.value)}
                            placeholder={t('platformUsers.designation_placeholder', 'e.g. Support Lead')}
                            className={`${field} border-gray-300`} />
                    </div>
                </div>

                <div className="flex items-center justify-end gap-2 px-5 py-4 border-t border-gray-100">
                    <button type="button" onClick={onClose} className="px-4 py-2 text-sm rounded-xl border border-gray-300 text-gray-600 hover:bg-gray-50">
                        {t('platformUsers.cancel', 'Cancel')}
                    </button>
                    <button type="button" onClick={submit} disabled={mutating} data-testid="add-platform-user-submit"
                        className="px-4 py-2 text-sm rounded-xl text-white font-semibold bg-gradient-to-r from-[#4ABDBD] to-[#2D8A8A] hover:shadow-lg disabled:opacity-60 flex items-center gap-2">
                        {mutating && <Loader2 className="w-4 h-4 animate-spin" />}
                        {isEdit ? t('platformUsers.save', 'Save changes') : t('platformUsers.create', 'Create user')}
                    </button>
                </div>
            </div>
        </div>
    );
}

// ---------------------------------------------------------------------------
// The platform's own team — the Super Admin's "All users" screen. The
// platform-level twin of the tenant app's Users page, and deliberately the same
// design: table treatment, role chips, status toggle, temp-password panel.
// ---------------------------------------------------------------------------
export default function PlatformUsers() {
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const { t } = useTranslation();
    const { staffList, staffTotal, staffListLoading, roles, mutating } = useSelector((s) => s.platformStaff);

    const [currentPage, setCurrentPage] = useState(1);
    const [itemsPerPage, setItemsPerPage] = useState(10);
    const [searchQuery, setSearchQuery] = useState('');
    const [debouncedSearch, setDebouncedSearch] = useState('');
    const [activeTab, setActiveTab] = useState('active');
    const [roleFilter, setRoleFilter] = useState('');
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [editing, setEditing] = useState(null);
    const [refreshKey, setRefreshKey] = useState(0);

    const searchTimeoutRef = useRef(null);

    useEffect(() => {
        if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);
        searchTimeoutRef.current = setTimeout(() => {
            setDebouncedSearch(searchQuery.trim());
            setCurrentPage(1);
        }, 500);
        return () => { if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current); };
    }, [searchQuery]);

    // The role list drives both the filter and the create form's dropdown.
    useEffect(() => { dispatch(getPlatformRoles({ limit: 100 })); }, [dispatch, refreshKey]);

    useEffect(() => {
        dispatch(getPlatformStaffList({
            page: currentPage,
            limit: itemsPerPage,
            search: debouncedSearch,
            status: activeTab,
            platformRoleId: roleFilter ? [roleFilter] : [],
        }));
    }, [dispatch, currentPage, itemsPerPage, debouncedSearch, activeTab, roleFilter, refreshKey]);

    const refetch = () => setRefreshKey((k) => k + 1);

    const handleStatusToggle = async (staff) => {
        const isActive = staff.status === 'active';
        const next = isActive ? 'inactive' : 'active';

        const result = await MySwal.fire({
            title: isActive
                ? t('platformUsers.deactivate_title', 'Deactivate this user?')
                : t('platformUsers.activate_title', 'Activate this user?'),
            text: isActive
                ? t('platformUsers.deactivate_text', 'They will be signed out of anything they have open and cannot sign in again until reactivated.')
                : t('platformUsers.activate_text', 'They will be able to sign in again straight away.'),
            icon: 'question',
            showCancelButton: true,
            confirmButtonColor: '#4ABDBD',
            cancelButtonColor: '#e5e7eb',
            confirmButtonText: t('platformUsers.confirm', 'Confirm'),
            cancelButtonText: t('platformUsers.cancel', 'Cancel'),
        });
        if (!result.isConfirmed) return;

        try {
            await dispatch(setPlatformStaffStatus({ platformStaffId: staff.platformStaffId, status: next })).unwrap();
            toast.success(next === 'active'
                ? t('platformUsers.activated', 'User activated')
                : t('platformUsers.deactivated', 'User deactivated'));
            refetch();
        } catch (err) {
            toast.warn(typeof err?.error === 'string' ? err.error : t('platformUsers.status_error', 'Failed to update the status'));
        }
    };

    const handleDelete = async (staff) => {
        const result = await MySwal.fire({
            title: t('platformUsers.delete_title', 'Delete this user?'),
            text: t('platformUsers.delete_text', 'Their login is removed. What they did stays in the audit log.'),
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#4ABDBD',
            cancelButtonColor: '#e5e7eb',
            confirmButtonText: t('platformUsers.delete', 'Delete'),
            cancelButtonText: t('platformUsers.cancel', 'Cancel'),
            showLoaderOnConfirm: true,
            preConfirm: async () => {
                try {
                    return await dispatch(deletePlatformStaff(staff.platformStaffId)).unwrap();
                } catch (err) {
                    toast.warn(typeof err?.error === 'string' ? err.error : t('platformUsers.delete_error', 'Failed to delete the user'));
                    throw err;
                }
            },
            allowOutsideClick: () => !MySwal.isLoading(),
        });
        if (result.isConfirmed) {
            toast.success(t('platformUsers.deleted', 'User deleted'));
            refetch();
        }
    };

    // A RESET NEVER PUTS A PASSWORD IN A MAIL OR ON A SCREEN (owner, 2 Oct
    // 2026): the server makes the current password stop working and tells the
    // person, by e-mail or SMS, to set a new one with Forgot password.
    const handleResetPassword = async (staff) => {
        const result = await MySwal.fire({
            title: t('passwordReset.confirm_title'),
            html: `<p style="font-size:14px;color:#475569">${t('passwordReset.confirm_text')}</p>
                   <p style="margin-top:10px;font-size:12px;color:#64748b">${t('passwordReset.sessions_note')}</p>`,
            icon: 'question',
            showCancelButton: true,
            confirmButtonColor: '#4ABDBD',
            cancelButtonColor: '#e5e7eb',
            confirmButtonText: t('passwordReset.confirm'),
            cancelButtonText: t('passwordReset.cancel'),
        });
        if (!result.isConfirmed) return;

        try {
            const res = await dispatch(resetPlatformStaffPassword({ platformStaffId: staff.platformStaffId })).unwrap();
            await MySwal.fire({
                title: t('passwordReset.done_title'),
                text: res?.data?.noticeDelivered === false
                    ? t('passwordReset.done_not_sent')
                    : t('passwordReset.done_sent'),
                icon: 'success',
                confirmButtonColor: '#4ABDBD',
                confirmButtonText: t('passwordReset.done'),
            });
        } catch (err) {
            toast.warn(typeof err?.error === 'string' ? err.error : t('passwordReset.error'));
        }
    };

    const openCreate = () => { setEditing(null); setIsModalOpen(true); };
    const openEdit = (staff) => { setEditing(staff); setIsModalOpen(true); };

    const handlePageChange = (page, newItemsPerPage) => {
        if (newItemsPerPage && newItemsPerPage !== itemsPerPage) {
            setItemsPerPage(newItemsPerPage);
            setCurrentPage(1);
        } else {
            setCurrentPage(page);
        }
    };

    const indexOfFirst = (currentPage - 1) * itemsPerPage;

    const initialsOf = (s) =>
        `${s.name ? s.name.charAt(0).toUpperCase() : ''}${s.surname ? s.surname.charAt(0).toUpperCase() : ''}`;

    return (
        <div data-testid="platform-users-page-container" className="bg-white p-4 rounded-2xl shadow-md sm:p-6 w-full">
            <div className="flex flex-col sm:flex-row justify-between items-center mb-6 gap-4">
                <div className="ml-2">
                    <h1 data-testid="platform-users-page-title" className="text-2xl sm:text-3xl font-bold bg-gradient-to-r from-[#4ABDBD] to-[#3AA8A8] bg-clip-text text-transparent">
                        {t('platformUsers.title', 'All users')}
                    </h1>
                    <p className="text-xs text-slate-400 mt-1">
                        {t('platformUsers.subtitle', 'The people who run the platform with you — support, onboarding, catalogues, oversight.')}
                    </p>
                </div>
                <div className="flex gap-2 w-full sm:w-auto">
                    <button
                        data-testid="manage-platform-roles-button"
                        onClick={() => navigate('/user-roles')}
                        className="flex items-center gap-2 px-4 py-2 rounded-xl border border-[#4ABDBD] text-[#2D8A8A] font-semibold text-sm hover:bg-[#4ABDBD]/10 cursor-pointer whitespace-nowrap"
                    >
                        <ShieldCheck className="w-4 h-4" />
                        {t('platformUsers.manage_roles', 'Roles')}
                    </button>
                    <button
                        data-testid="add-platform-user-button"
                        onClick={openCreate}
                        disabled={staffListLoading}
                        className={`group flex items-center gap-2 w-full sm:w-auto cursor-pointer px-4 py-2 bg-gradient-to-r from-[#4ABDBD] via-[#3AA8A8] to-[#2D8A8A] text-white rounded-xl shadow-lg hover:shadow-xl hover:scale-105 transition-all duration-300 font-semibold text-sm whitespace-nowrap ${staffListLoading ? 'opacity-50 cursor-not-allowed' : ''}`}
                    >
                        <Plus className="w-5 h-5" />
                        {t('platformUsers.add_user', 'Add user')}
                    </button>
                </div>
            </div>

            <div className="mb-6 flex flex-col sm:flex-row gap-2 sm:items-center">
                <div className="relative flex bg-gradient-to-r from-gray-50 to-blue-50/30 p-1 rounded-2xl shadow border border-gray-200 gap-2">
                    {['active', 'inactive'].map((tab) => (
                        <button
                            key={tab}
                            data-testid={`platform-users-tab-${tab}`}
                            onClick={() => { setActiveTab(tab); setCurrentPage(1); }}
                            className={`relative px-6 py-2 text-sm rounded-xl transition-colors duration-300 flex-1 z-10 ${activeTab === tab ? 'text-white' : 'text-gray-600 hover:text-[#4ABDBD] cursor-pointer'}`}
                        >
                            {activeTab === tab && (
                                <Motion.div
                                    layoutId="activePlatformUserStatusTab"
                                    className="absolute inset-0 bg-gradient-to-r from-[#4ABDBD] via-[#3AA8A8] to-[#2D8A8A] rounded-xl shadow-lg !m-0"
                                    transition={{ type: 'spring', bounce: 0.2, duration: 0.6 }}
                                />
                            )}
                            <span className="relative z-20">
                                {tab === 'active' ? t('platformUsers.active', 'Active') : t('platformUsers.inactive', 'Inactive')}
                            </span>
                        </button>
                    ))}
                </div>

                <div className="relative w-full sm:w-72">
                    <input
                        data-testid="platform-users-search-input"
                        type="text"
                        placeholder={t('platformUsers.search_placeholder', 'Search name / email / id…')}
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value.replace(/[<>]/g, '').slice(0, 50))}
                        maxLength={50}
                        className="w-full p-2 pl-10 pr-8 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#4ABDBD]"
                    />
                    <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-[#4ABDBD]" />
                </div>

                <select
                    data-testid="platform-users-role-filter"
                    value={roleFilter}
                    onChange={(e) => { setRoleFilter(e.target.value); setCurrentPage(1); }}
                    className="w-full sm:w-52 p-2 border border-gray-300 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-[#4ABDBD]"
                >
                    <option value="">{t('platformUsers.all_roles', 'All roles')}</option>
                    {(roles || []).map((r) => (
                        <option key={r.platformRoleId} value={r.platformRoleId}>{r.name}</option>
                    ))}
                </select>
            </div>

            {staffListLoading ? (
                <div className="flex flex-col items-center justify-center py-4 pt-12">
                    <CommonLoader />
                    <span className="text-xl text-[#4ABDBD]">{t('platformUsers.loading', 'Loading...')}</span>
                </div>
            ) : (
                <>
                    <div className="hidden sm:block overflow-x-auto rounded-md border border-gray-200">
                        <table data-testid="platform-users-table" className="w-full border-collapse rounded-md overflow-hidden">
                            <thead>
                                <tr className="bg-gradient-to-r from-[#4ABDBD] via-[#3AA8A8] to-[#2D8A8A] text-white">
                                    <th className="p-3 text-left text-xs font-bold uppercase tracking-wider">{t('platformUsers.serial_no', 'S.No')}</th>
                                    <th className="p-3 text-left text-xs font-bold uppercase tracking-wider">{t('platformUsers.name', 'Name')}</th>
                                    <th className="p-3 text-left text-xs font-bold uppercase tracking-wider">{t('platformUsers.role', 'Role')}</th>
                                    <th className="p-3 text-left text-xs font-bold uppercase tracking-wider">{t('platformUsers.contact', 'Contact')}</th>
                                    <th className="p-3 text-left text-xs font-bold uppercase tracking-wider">{t('platformUsers.status', 'Status')}</th>
                                    <th className="p-3 text-center text-xs font-bold uppercase tracking-wider">{t('platformUsers.action', 'Action')}</th>
                                </tr>
                            </thead>
                            <tbody data-testid="platform-users-table-body">
                                {(staffList || []).length > 0 ? staffList.map((s, index) => {
                                    const isActive = s.status === 'active';
                                    return (
                                        <tr key={s.platformStaffId} data-testid={`platform-user-row-${s.platformStaffId}`} className="border-b border-gray-100/50 hover:bg-gradient-to-r hover:from-blue-50/80 hover:to-cyan-50/80 transition-all duration-300 group">
                                            <td className="p-3 text-xs text-gray-600">{indexOfFirst + index + 1}</td>
                                            <td className="p-3 text-xs text-gray-600">
                                                <div className="flex items-center gap-3">
                                                    <div className="w-10 h-10 flex-shrink-0 rounded-full bg-[#4ABDBD] flex items-center justify-center shadow-md">
                                                        {initialsOf(s)
                                                            ? <span className="text-white text-sm font-bold uppercase">{initialsOf(s)}</span>
                                                            : <User className="w-5 h-5 text-white" />}
                                                    </div>
                                                    <div>
                                                        <div className="font-semibold text-gray-700">{`${s.name || ''} ${s.surname || ''}`.trim() || '-'}</div>
                                                        {s.designation && <div className="text-[11px] text-slate-400">{s.designation}</div>}
                                                    </div>
                                                </div>
                                            </td>
                                            <td className="p-3 text-xs">
                                                <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-[#4ABDBD]/10 text-[#2D8A8A] text-[11px] font-semibold">
                                                    {s.roleName || '-'}
                                                </span>
                                                {/* A person attached to a switched-off role can sign in
                                                    to nothing — this is the only place they'd learn why. */}
                                                {s.roleStatus === 'inactive' && (
                                                    <div className="text-[10px] text-amber-600 mt-1">{t('platformUsers.role_inactive', 'role is inactive')}</div>
                                                )}
                                            </td>
                                            <td className="p-3 text-xs text-gray-600">
                                                <div>{s.email || '-'}</div>
                                                <div className="text-[11px] text-slate-400">{s.phone || ''}</div>
                                            </td>
                                            <td className="p-3 text-xs">
                                                <div className="flex items-center gap-2">
                                                    <button
                                                        type="button" role="switch" aria-checked={isActive}
                                                        data-testid={`platform-user-status-toggle-${s.platformStaffId}`}
                                                        onClick={() => !mutating && handleStatusToggle(s)}
                                                        disabled={mutating}
                                                        className={`relative inline-flex h-6 w-11 flex-shrink-0 items-center rounded-full transition-colors duration-300 ${isActive ? 'bg-gradient-to-r from-[#4ABDBD] to-[#2D8A8A]' : 'bg-gray-300'} ${mutating ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
                                                    >
                                                        <span className={`inline-block h-4 w-4 transform rounded-full bg-white shadow transition-transform duration-300 ${isActive ? 'translate-x-6' : 'translate-x-1'}`} />
                                                    </button>
                                                    <span className={`text-xs font-semibold ${isActive ? 'text-green-700' : 'text-gray-500'}`}>
                                                        {isActive ? t('platformUsers.active', 'Active') : t('platformUsers.inactive', 'Inactive')}
                                                    </span>
                                                </div>
                                            </td>
                                            <td className="p-3 text-xs">
                                                <div className="flex justify-center gap-2">
                                                    <button data-testid={`edit-platform-user-${s.platformStaffId}`} onClick={() => openEdit(s)}
                                                        title={t('platformUsers.edit', 'Edit')}
                                                        className="text-[#4ABDBD] hover:text-white p-2 rounded-xl bg-gradient-to-r from-blue-50 to-cyan-50 hover:from-[#4ABDBD] hover:to-[#3AA8A8] shadow-md cursor-pointer transition-all duration-300 hover:scale-110">
                                                        <Pencil className="h-4 w-4" />
                                                    </button>
                                                    <button data-testid={`reset-platform-password-${s.platformStaffId}`} onClick={() => handleResetPassword(s)}
                                                        title={t('platformUsers.reset_password', 'Reset password')}
                                                        className="text-amber-500 hover:text-white p-2 rounded-xl bg-gradient-to-r from-amber-50 to-orange-50 hover:from-amber-500 hover:to-orange-500 shadow-md cursor-pointer transition-all duration-300 hover:scale-110">
                                                        <KeyRound className="h-4 w-4" />
                                                    </button>
                                                    <button data-testid={`delete-platform-user-${s.platformStaffId}`} onClick={() => handleDelete(s)}
                                                        disabled={mutating}
                                                        title={t('platformUsers.delete', 'Delete')}
                                                        className={`text-red-500 hover:text-white p-2 rounded-xl bg-gradient-to-r from-red-50 to-pink-50 hover:from-red-500 hover:to-pink-500 shadow-md cursor-pointer transition-all duration-300 hover:scale-110 ${mutating ? 'opacity-50 cursor-not-allowed' : ''}`}>
                                                        <Trash2 className="h-4 w-4" />
                                                    </button>
                                                </div>
                                            </td>
                                        </tr>
                                    );
                                }) : (
                                    <tr>
                                        <td colSpan={6} className="p-6 text-center text-gray-500">
                                            {t('platformUsers.no_users', 'No platform users yet. Add the people who run the platform with you to give them a login.')}
                                        </td>
                                    </tr>
                                )}
                            </tbody>
                        </table>
                    </div>

                    {/* Cards on mobile */}
                    <div className="block sm:hidden space-y-4">
                        {(staffList || []).length > 0 ? staffList.map((s) => {
                            const isActive = s.status === 'active';
                            return (
                                <div key={s.platformStaffId} data-testid={`platform-user-card-${s.platformStaffId}`} className="p-4 bg-gray-50 rounded-xl shadow-md">
                                    <div className="flex justify-between items-start mb-2">
                                        <div>
                                            <div className="font-semibold text-gray-800 text-sm">{`${s.name || ''} ${s.surname || ''}`.trim() || '-'}</div>
                                            <span className="inline-flex mt-1 px-2 py-0.5 rounded-full bg-[#4ABDBD]/10 text-[#2D8A8A] text-[11px] font-semibold">
                                                {s.roleName || '-'}
                                            </span>
                                        </div>
                                        <div className="flex gap-2">
                                            <button onClick={() => openEdit(s)} className="text-[#4ABDBD] p-2 rounded-xl bg-blue-50"><Pencil className="h-4 w-4" /></button>
                                            <button onClick={() => handleResetPassword(s)} className="text-amber-500 p-2 rounded-xl bg-amber-50"><KeyRound className="h-4 w-4" /></button>
                                            <button onClick={() => handleDelete(s)} className="text-red-500 p-2 rounded-xl bg-red-50"><Trash2 className="h-4 w-4" /></button>
                                        </div>
                                    </div>
                                    <div className="text-xs text-gray-600">{s.email || '-'}</div>
                                    <div className="text-xs text-gray-600">{s.phone || ''}</div>
                                    <div className="text-xs flex items-center gap-2 mt-3">
                                        <button type="button" role="switch" aria-checked={isActive}
                                            onClick={() => !mutating && handleStatusToggle(s)} disabled={mutating}
                                            className={`relative inline-flex h-6 w-11 items-center rounded-full ${isActive ? 'bg-gradient-to-r from-[#4ABDBD] to-[#2D8A8A]' : 'bg-gray-300'}`}>
                                            <span className={`inline-block h-4 w-4 transform rounded-full bg-white shadow transition-transform ${isActive ? 'translate-x-6' : 'translate-x-1'}`} />
                                        </button>
                                        <span className={`font-semibold ${isActive ? 'text-green-700' : 'text-gray-500'}`}>
                                            {isActive ? t('platformUsers.active', 'Active') : t('platformUsers.inactive', 'Inactive')}
                                        </span>
                                    </div>
                                </div>
                            );
                        }) : (
                            <div className="p-6 text-center text-gray-500 text-sm">
                                {t('platformUsers.no_users', 'No platform users yet. Add the people who run the platform with you to give them a login.')}
                            </div>
                        )}
                    </div>
                </>
            )}

            {!staffListLoading && staffTotal > 0 && (
                <Pagination
                    totalItems={staffTotal}
                    currentPage={currentPage}
                    itemsPerPage={itemsPerPage}
                    onPageChange={handlePageChange}
                />
            )}

            <AddPlatformUser
                isOpen={isModalOpen}
                editing={editing}
                roles={roles}
                onClose={() => { setIsModalOpen(false); setEditing(null); }}
                onSaved={refetch}
            />
        </div>
    );
}
